import JSZip from 'jszip';
import { imageAPI, imageArchive } from '@fluke/baseline-client-apis';

import { getSecureGetUrl } from '@plat/domain-api';
import {
  deleteFromDomainAPI,
  getImagesIndexFromDomainAPI,
  getListFilesFromDomainAPI,
  sendJSONListToDomainAPI,
  sendZipToDomainAPI,
} from '../../slices/columbiaSlice';
import {
  setSelectedImageIds,
  addImageToWorkItem,
  setSelectedImagesForWorkItem,
} from '../../slices/imagesSlice';
import { useAppDispatch } from '../../configureStore';
import remove_duplicates_from_list from '../../../utils/remove_duplicates_from_list';
import routerPathEnum from '../../../enums/routerPathEnum';
import { UserContext } from '../../../context';
import { useContext } from 'react';

export function get_files_from_domain_api() {
  return async (dispatch) => {
    /**
     * get all the files from Columbia S3
     * files will include:
     *    previous selected images as a zip (zip_files)
     *    all image ids as an array (images_index)
     */
    const images_index = await getImagesIndexFromDomainAPI();
    if (!images_index && !images_index.length) {
      sendJSONListToDomainAPI([]);
    }

    /**
     * set the selected images to be the images that come from the Columbia S3
     */

    dispatch(setSelectedImageIds(images_index));
  };
}

export function build_all_images_index() {
  let all_images_index = [];
  return getListFilesFromDomainAPI().then((files) => {
    if (files) {
      files.forEach(({ key }) => {
        const split_key = key.split('.');
        if (split_key[1] === 'zip') {
          const file_id = split_key[0].split('baseline_image_data_')[1];
          all_images_index.push(file_id);
        }
      });
      return all_images_index;
    }
  });
}

/**
 * 1. Get files from DomainAPI
 * 2. Read contents of images_index.json
 * 3. For each file name key
 *    3a. iterate through images_index to see if it exists
 *    3b. add the id if it doesn't
 */
export async function update_auto_linked_files() {
  const dispatch = useAppDispatch();
  let all_images_index = [];
  /**
   * build an all_images list that contains all the zip file names in s3
   */
  build_all_images_index().then((list) => {
    all_images_index = list;

    getImagesIndexFromDomainAPI().then((images_index) => {
      let new_ids_difference = all_images_index.filter((x) => !images_index.includes(x));
      let new_images_index = images_index;
      if (new_ids_difference) {
        new_images_index = [...new_images_index, ...new_ids_difference];
      }

      let removed_ids_difference = images_index.filter((x) => !all_images_index.includes(x));

      if (removed_ids_difference && new_images_index) {
        removed_ids_difference.forEach((id) => {
          new_images_index.splice(
            new_images_index.findIndex((list_id) => list_id === id),
            1,
          );
        });
      }

      sendJSONListToDomainAPI(new_images_index).then((result) => {
        if (result === 'OK') {
          new_images_index.forEach((id) => {
            getSecureGetUrl(`baseline_image_data_${id}.zip`).then(({ url }) => {
              dispatch(store_image_in_redux_by_url(url));
            });
          });
        }
      });
    });
  });
}

export function remove_image_from_columbia(id) {
  return async (dispatch, getState) => {
    const { selectedImageIds, workItemSelectedImages } = getState().images;
    let new_work_item_images = [...workItemSelectedImages];
    let new_images_index = [...selectedImageIds];

    new_images_index.splice(
      new_images_index.findIndex((image_id) => image_id === id),
      1,
    );

    new_work_item_images.forEach((image, index) => {
      if (image.imageJSON.main.image_id === id) {
        new_work_item_images.splice(index, 1);
      }
    });

    deleteFromDomainAPI(id).then((result) => {
      if (result === 'No Content') {
        sendJSONListToDomainAPI(new_images_index).then((result) => {
          if (result === 'OK') {
            dispatch(setSelectedImageIds(new_images_index));
            dispatch(setSelectedImagesForWorkItem(new_work_item_images));
          }
        });
      }
    });
  };
}

function store_image_in_redux_by_url(url) {
  return async (dispatch) => {
    if (url) {
      imageArchive.fetchImageArchive(url).then((unzipped) => {
        imageArchive.unpackImageArchive(unzipped).then((image_archive) => {
          dispatch(addImageToWorkItem(image_archive));
        });
      });
    } else {
      console.error('URL not found: ', url);
      return;
    }
  };
}

export function store_image_s3_and_redux_by_id(id, new_images_index, history) {
  const jszip = new JSZip();
  const dispatch = useAppDispatch();
  const { userSettings: { currentOrganization = '' } = {} } = useContext(UserContext);

  imageAPI.fetchColumbiaInfoById(id, currentOrganization).then((zip) => {
    if (zip) {
      sendZipToDomainAPI(id, zip.data).then((result) => {
        if (result) {
          jszip.loadAsync(zip.data).then((unzipped) => {
            imageArchive.unpackImageArchive(unzipped).then((image_archive) => {
              sendJSONListToDomainAPI(new_images_index).then((result) => {
                if (result === 'OK') {
                  dispatch(addImageToWorkItem(image_archive));
                  history.push(routerPathEnum.WORK_ITEM);
                } else {
                  return console.error('Error: JSON Not Sent to API');
                }
              });
            });
          });
        }
      });
    } else {
      return console.error('Error: Image Not Found');
    }
  });
}

export function store_updated_image_to_s3_and_redux(id, history) {
  const jszip = new JSZip();
  const { userSettings: { currentOrganization = '' } = {} } = useContext(UserContext);
  remove_image_from_columbia(id);

  return async (dispatch, getState) => {
    const { workItemSelectedImages, wasImageUpdated } = getState().images;

    if (wasImageUpdated) {
      let new_work_item_images = [...workItemSelectedImages];

      imageAPI.fetchColumbiaInfoById(id, currentOrganization).then((zip) => {
        if (zip) {
          sendZipToDomainAPI(id, zip.data).then((result) => {
            if (result) {
              jszip.loadAsync(zip.data).then((unzipped) => {
                imageArchive.unpackImageArchive(unzipped).then((image_archive) => {
                  new_work_item_images.forEach((image, index) => {
                    if (image.imageJSON.main.image_id === id) {
                      delete new_work_item_images[index];
                      new_work_item_images[index] = image_archive;
                    }
                  });
                  dispatch(setSelectedImagesForWorkItem(new_work_item_images));
                  history.push(routerPathEnum.WORK_ITEM);
                });
              });
            }
          });
        }
      });
    } else {
      history.push(routerPathEnum.WORK_ITEM);
    }
  };
}

export function add_images_to_s3_by_id(list, history) {
  /**
   * build an all_images list that contains all the zip file names in s3
   */
  return build_all_images_index().then((all_images_index) => {
    let new_images_index = remove_duplicates_from_list(list);
    let new_ids_difference = new_images_index.filter((x) => !all_images_index.includes(x));
    new_ids_difference.forEach((id) => {
      store_image_s3_and_redux_by_id(id, new_images_index, history);
    });
  });
}
